import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthContext'
import {
  canManageStateRegulatory,
  getStateRegulatoryBodies,
  type StateRegulatoryBody,
} from '../../../lib/services/stateRegulatory'
import { StateDetailPanel } from './StateDetailPanel'

const INGESTION_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  validated: 'bg-teal-100 text-teal-800',
  parsed: 'bg-yellow-100 text-yellow-800',
  sourced: 'bg-gray-100 text-gray-600',
  not_started: 'bg-gray-50 text-gray-400',
}

export function StateRegulatoryPage() {
  const { viewerContext } = useAuth()
  const [selectedState, setSelectedState] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const canManage = canManageStateRegulatory(viewerContext)

  const { data: bodies, isLoading, error } = useQuery({
    queryKey: ['state-regulatory-bodies'],
    queryFn: getStateRegulatoryBodies,
    enabled: canManage,
  })

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        State regulatory management is restricted to superadmin role.
      </div>
    )
  }

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500">Loading state regulatory data...</div>
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
        Failed to load state regulatory data: {(error as Error).message}
      </div>
    )
  }

  const filtered = statusFilter === 'all'
    ? bodies
    : bodies?.filter((b) => b.is_active ? statusFilter === 'active' : statusFilter !== 'active')

  const activeCount = bodies?.filter((b) => b.is_active).length ?? 0

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
              State Regulatory Bodies
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {bodies?.length ?? 0} states tracked &middot; {activeCount} active
            </p>
          </div>
          <div className="flex gap-2">
            <select
              className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All States</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive / Pending</option>
            </select>
          </div>
        </div>
      </header>

      <section className="v11-section overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">State</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Agency</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Jurisdiction</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Citation</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(filtered ?? []).map((body) => (
              <StateRow
                key={body.state_code}
                body={body}
                isSelected={selectedState === body.state_code}
                onSelect={() => setSelectedState(
                  selectedState === body.state_code ? null : body.state_code
                )}
              />
            ))}
          </tbody>
        </table>
        {(!filtered || filtered.length === 0) && (
          <div className="p-6 text-center text-gray-400">No states match the filter.</div>
        )}
      </section>

      {selectedState && (
        <StateDetailPanel
          stateCode={selectedState}
          onClose={() => setSelectedState(null)}
        />
      )}
    </div>
  )
}

function StateRow({
  body,
  isSelected,
  onSelect,
}: {
  body: StateRegulatoryBody
  isSelected: boolean
  onSelect: () => void
}) {
  const statusClass = body.is_active ? INGESTION_BADGE.active : INGESTION_BADGE.sourced

  return (
    <tr className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
      <td className="px-4 py-3 font-medium">{body.state_code}</td>
      <td className="px-4 py-3">
        <div>{body.agency_name}</div>
        {body.agency_abbreviation && (
          <div className="text-xs text-gray-400">{body.agency_abbreviation}</div>
        )}
      </td>
      <td className="px-4 py-3 capitalize">{body.jurisdiction_scope.replace('_', ' ')}</td>
      <td className="px-4 py-3 text-xs text-gray-500">{body.licensing_authority_citation ?? '—'}</td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
          {body.is_active ? 'Active' : 'Pending'}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          className="btn-ghost text-xs"
          onClick={onSelect}
        >
          {isSelected ? 'Close' : 'View'}
        </button>
      </td>
    </tr>
  )
}
